/**
 * Supabase client instanciado para o app CRM.
 *
 * MUDANÇA M1 Fase 3 (2026-04-19):
 * - Delegado ao @pps/auth-client (factory unificada).
 * - Storage adapter: capacitorStorage (detecta native, fallback localStorage).
 *   Antes usava sessionStorage — sessão não persistia entre restarts do app
 *   nativo. Comportamento agora correto e consistente com o app public.
 *
 * FIX M-1 (2026-05-02): SSO cross-subdomain web.
 * - Em plataforma nativa, continua usando capacitorStorage (Keychain/Keystore).
 * - Em plataforma web, monta um adapter via `createWebStorage({ cookieDomain })`
 *   quando `VITE_AUTH_COOKIE_DOMAIN` está definido (ex: `.playprosport.com.br`
 *   em HML/PROD). Isso grava a sessão em cookie compartilhado no domínio pai
 *   habilitando SSO entre `app.*` (public) e `crm.*` (admin). Sem a env var,
 *   cai em localStorage por-origem (sem SSO) — comportamento esperado em dev.
 *
 * `createWebStorage` em `localhost` recusa o cookie silenciosamente e volta
 * pra localStorage automaticamente, então não há regressão em dev.
 */
import { Capacitor } from "@capacitor/core";
import {
  IMPERSONATION_AUTH_STORAGE_KEY,
  captureImpersonationFromUrl,
  chaveDaSessaoDoAmbiente,
  createImpersonationStorage,
  createSupabaseClient,
  isImpersonationTab,
  type AuthStorage,
} from "@pps/auth-client";
import { capacitorStorage } from "@pps/auth-client/storage/capacitor";
import { createWebStorage } from "@pps/auth-client/storage/web";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const COOKIE_DOMAIN = import.meta.env.VITE_AUTH_COOKIE_DOMAIN as string | undefined;
// Nome fixo da sessão (localStorage, cookie SSO e storage nativo). Sem a
// variável: nome derivado do host da API, como sempre. Existe para a troca de
// URL da API não deslogar ninguém — ver packages/auth-client/src/storage-key.ts.
const SESSION_STORAGE_KEY = chaveDaSessaoDoAmbiente(
  import.meta.env.VITE_SUPABASE_AUTH_STORAGE_KEY as string | undefined
);

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Aba de suporte (impersonação de owner pelo admin): a marca vem de
// `?impersonation=<id>` e precisa ser lida ANTES de criar o client — o
// storage depende dela. Doc: docs/funcionalidades/impersonacao-de-owner.md
captureImpersonationFromUrl();

/**
 * Na aba de suporte a sessão do owner vive SÓ em sessionStorage, numa chave
 * própria: nunca no cookie compartilhado do domínio pai (sobrescreveria a
 * sessão do admin e de qualquer outra aba) nem no localStorage do origin.
 */
function pickStorage(): { storage: AuthStorage; storageKey?: string } {
  if (Capacitor.isNativePlatform()) return { storage: capacitorStorage };
  if (isImpersonationTab()) {
    return {
      storage: createImpersonationStorage(),
      storageKey: IMPERSONATION_AUTH_STORAGE_KEY,
    };
  }
  return { storage: createWebStorage({ cookieDomain: COOKIE_DOMAIN ?? undefined }) };
}

const picked = pickStorage();

export const supabase = createSupabaseClient({
  url: SUPABASE_URL,
  anonKey: SUPABASE_PUBLISHABLE_KEY,
  storage: picked.storage,
  // A aba de suporte tem chave própria (nunca a da sessão real).
  storageKey: picked.storageKey ?? SESSION_STORAGE_KEY,
});
